<template>
  <Teleport to="body">
    <Transition name="menu-fade">
      <div v-if="isOpen" class="mega-menu-overlay" @click.self="closeMenu">
        <Transition name="menu-slide">
          <div v-if="isOpen" class="mega-menu-container">
            <header class="mega-menu-header">
              <h2 class="mega-menu-title">Navigation</h2>
              <button class="close-button" @click="closeMenu" aria-label="Fermer le menu">
                <IonIcon :icon="closeOutline" />
              </button>
            </header>

            <div class="mega-menu-content">
              <div v-if="loading" class="loading-state">
                <IonSpinner name="crescent" />
                <span>Chargement...</span>
              </div>

              <div v-else-if="modulesWithLinks.length > 0" class="menu-categories">
                <CategoriesList
                  :categories="modulesWithLinks"
                  :is-open="true"
                  @link-click="closeMenu"
                />
              </div>

              <div v-else class="empty-state">
                <p>Aucune catégorie disponible</p>
              </div>
            </div>

            <div class="mega-menu-footer">
              <UserRoutesGrid
                :user-routes="displayRoutes"
                :is-expanded="true"
                @route-click="closeMenu"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { IonIcon, IonSpinner } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { useModuleApi } from '@/features/menu-management/composables/useModuleApi';
import { useUserRoutes } from '@/common/composables/useUserRoutes';
import CategoriesList from '@/layouts/components/category-collapse/CategoriesList.vue';
import UserRoutesGrid from '@/layouts/components/UserRoutesGrid.vue';

interface Props {
  isOpen: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { modulesWithLinks, loading } = useModuleApi();
const { displayRoutes } = useUserRoutes();

function closeMenu() {
  emit('close');
}
</script>

<style scoped>
/* Overlay */
.mega-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: flex-start;
}

/* Menu Container */
.mega-menu-container {
  width: 85%;
  max-width: 320px;
  height: 100%;
  background: var(--ion-background-color);
  display: flex;
  flex-direction: column;
  box-shadow: var(--ion-box-shadow-hover);
}

/* Header */
.mega-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: var(--ion-color-primary);
  color: white;
  flex-shrink: 0;
}

.mega-menu-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  font-size: 24px;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Content */
.mega-menu-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
}

.mega-menu-content::-webkit-scrollbar {
  width: 4px;
}

.mega-menu-content::-webkit-scrollbar-track {
  background: transparent;
}

.mega-menu-content::-webkit-scrollbar-thumb {
  background-color: var(--ion-color-medium);
  border-radius: 2px;
}

/* Menu Categories */
.menu-categories {
  padding: 8px 0;
}

/* Footer */
.mega-menu-footer {
  flex-shrink: 0;
  padding: 12px 16px;
  border-top: 1px solid var(--ion-border-color);
  background: var(--ion-background-color);
  box-shadow: var(--ion-box-shadow);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  color: var(--ion-color-medium);
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: var(--ion-color-medium);
}

/* Transitions */
.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.3s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
}

.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: transform 0.3s ease;
}

.menu-slide-enter-from,
.menu-slide-leave-to {
  transform: translateX(-100%);
}
</style>
